import { Device, DeviceMediaRequest } from "@app/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { ValidationError } from "class-validator";
import { ModelHelpers } from "../../hooks";
import { formatByteSize, hasVideoRecompress } from "../../helpers";
import { CheckboxInput, InputLabel, InputRow, NumericInput, ValidatedInputGroup } from "./inputs";
import { LabeledText } from "./LabeledText";
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

type EditDeviceMediaRequestProps = {
    request: Partial<DeviceMediaRequest>;
    helpers: ModelHelpers<DeviceMediaRequest>;
    device: Device;
    zone: string | undefined,
    minStart: Date | undefined;
    maxEnd: Date | undefined;
};

const pad = (n: number) => String(n).padStart(2, "0");
const toDatePart = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toTimePart = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

/** Combine the values of a native date input (yyyy-MM-dd) and time input (HH:mm[:ss]) into a
 *  Date whose local fields equal those parts. Returns null when the date is incomplete. */
const combineParts = (datePart: string, timePart: string): Date | null => {
    if (!datePart) return null;
    const [y, mo, d] = datePart.split("-").map(Number);
    const [h = 0, mi = 0, s = 0] = (timePart || "00:00:00").split(":").map(Number);
    if ([y, mo, d].some(Number.isNaN)) return null;
    return new Date(y, mo - 1, d, h, mi, s);
};

type SplitDateTimeInputProps = {
    label?: string;
    /** Value expressed in the facility timezone's wall-clock (i.e. already display-converted). */
    value?: Date;
    onChange: (wallClock: Date | null) => void;
    disabled?: boolean;
    fieldName?: string;
    validationError?: ValidationError;
    validateModel?: () => void;
};

/**
 * A native date field plus a native time field with second precision (`step={1}`). Native inputs
 * never auto-snap or jump, so the value only ever changes when the user changes it. Validation
 * errors come from the model (class-validator) via {@link ValidatedInputGroup}.
 */
const SplitDateTimeInput: React.FC<SplitDateTimeInputProps> = ({ label, value, onChange, disabled, fieldName, validationError, validateModel }) => {
    const datePart = value ? toDatePart(value) : "";
    const timePart = value ? toTimePart(value) : "";
    const invalid = !!validationError;

    return (
        <ValidatedInputGroup fieldName={fieldName} validationError={validationError} validate={validateModel}>
            {({ handleBlur }) => (
                <div className="w-full">
                    {label && <InputLabel>{label}</InputLabel>}
                    <div className="flex flex-row space-x-2">
                        <input
                            type="date"
                            disabled={disabled}
                            className={cn("omnisight w-1/2", invalid && "border-brand-destructive")}
                            value={datePart}
                            onChange={(e) => onChange(combineParts(e.target.value, timePart || "00:00:00"))}
                            onBlur={handleBlur}
                        />
                        <input
                            type="time"
                            step={1}
                            disabled={disabled}
                            className={cn("omnisight w-1/2", invalid && "border-brand-destructive")}
                            value={timePart}
                            onChange={(e) => onChange(combineParts(datePart, e.target.value))}
                            onBlur={handleBlur}
                        />
                    </div>
                </div>
            )}
        </ValidatedInputGroup>
    );
};

export const EditDeviceMediaRequest: React.FC<EditDeviceMediaRequestProps> = ({ request, helpers, device, minStart, maxEnd, zone }) => {
    const defaultBps = 500000;
    const defaultFps = 20;
    const defaultWidth = 640;
    const defaultHeight = 480;

    // Approximate sizes of the non-video artifacts a request bundles.
    const radarBytesPerMinute = 1024 * 1024; // ~1 MB / min of radar data
    const configFilesBytes = 10 * 1024;      // ~10 KB support/config zip

    const [recompressVideo, setRecompressVideo] = useState(false);

    // Timezone the times are shown/entered in. Fall back to UTC (never the browser zone) when the
    // facility zone is unavailable, so the displayed time is unambiguous.
    const displayZone = zone ?? "UTC";

    const estimatedSizeBytes = useMemo(() => {
        if (!request.periodStart || !request.periodEnd) return undefined;
        const seconds = Math.max(0, (request.periodEnd.getTime() - request.periodStart.getTime()) / 1000);

        let bytes = configFilesBytes;
        if (request.includeVideo) {
            const bitrateBps = request.desiredBps || device?.dvrBitrateBps || defaultBps;
            bytes += seconds * (bitrateBps / 8);
        }
        if (request.includeRadarData) {
            bytes += (seconds / 60) * radarBytesPerMinute;
        }
        return bytes;
    }, [request.periodStart, request.periodEnd, request.desiredBps, request.includeVideo, request.includeRadarData, device?.dvrBitrateBps]);


    const handleWidthOnChange = useCallback(
        (newWidth?: number) => {
            if (newWidth && newWidth > (device.dvrResolutionX || defaultWidth)) {
                newWidth = device.dvrResolutionX || defaultWidth;
            }
            helpers.updateModel("desiredWidth", newWidth);
        },
        [device.dvrResolutionX, helpers.updateModel]
    );

    const handleHeightOnChange = useCallback(
        (newHeight?: number) => {
            if (newHeight && newHeight > (device.dvrResolutionY || defaultHeight)) {
                newHeight = device.dvrResolutionY || defaultHeight;
            }
            helpers.updateModel("desiredHeight", newHeight);
        },
        [device.dvrResolutionY, helpers.updateModel]
    );

    const handleFpsOnChange = useCallback(
        (newFps?: number) => {
            if (newFps && newFps > (device.dvrFps || defaultFps)) {
                newFps = device.dvrFps || defaultFps;
            }
            helpers.updateModel("desiredFps", newFps);
        },
        [device.dvrFps, helpers.updateModel]
    );

    const handleBpsOnChange = useCallback(
        (newBps?: number) => {
            if (newBps && newBps > (device.dvrBitrateBps || defaultBps)) {
                newBps = device.dvrBitrateBps || defaultBps;
            }
            helpers.updateModel("desiredBps", newBps);
        },
        [device.dvrBitrateBps, helpers.updateModel]
    );

    const toZonedTime = useCallback(
        (date: Date): Date => zonedTimeToUtc(date, displayZone),
        [displayZone]
    );

    const toDisplayTime = useCallback(
        (date: Date): Date => utcToZonedTime(date, displayZone),
        [displayZone]
    );

    // Commit handlers: store exactly what the user picked (seconds preserved). No auto-snapping of
    // seconds and no auto-bumping of the partner field — an invalid range (start after end) is
    // caught by the model's class-validator decorator (@DateBefore, generated from the server
    // [DateBefore] attribute), which blocks Save and surfaces the inline error.
    const handlePeriodStartOnChange = useCallback(
        (newTime: Date | null) => {
            helpers.updateModel("periodStart", newTime ? toZonedTime(newTime) : undefined);
        },
        [helpers.updateModel, toZonedTime]
    );

    const handlePeriodEndOnChange = useCallback(
        (newTime: Date | null) => {
            helpers.updateModel("periodEnd", newTime ? toZonedTime(newTime) : undefined);
        },
        [helpers.updateModel, toZonedTime]
    );

    useEffect(() => {
        if (!recompressVideo) {
            //Clear out custom values
            helpers.updateModel("desiredBps", undefined);
            helpers.updateModel("desiredFps", undefined);
            helpers.updateModel("desiredHeight", undefined);
            helpers.updateModel("desiredWidth", undefined);
        }
    }, [recompressVideo]);

    const tzAbbrev = formatInTimeZone(request.periodStart ?? new Date(), displayZone, "zzz");

    const startLabel = minStart ? `Period Start (Earliest: ${formatInTimeZone(minStart, displayZone, "MM/dd/yyyy HH:mm z")})` : 'Period Start';
    const endLabel = maxEnd ? `Period End (Latest: ${formatInTimeZone(maxEnd, displayZone, "MM/dd/yyyy HH:mm z")})` : 'Period End';

    const startDisplay = request.periodStart ? toDisplayTime(request.periodStart) : undefined;
    const endDisplay = request.periodEnd ? toDisplayTime(request.periodEnd) : undefined;

    // Validation comes from the model (class-validator decorators generated from the C# data
    // annotations) — the @DateBefore on periodStart enforces start <= end.
    const startValidationError = helpers.bindingsFor("periodStart").validationError;
    const endValidationError = helpers.bindingsFor("periodEnd").validationError;
    const hasPeriodError = !!startValidationError || !!endValidationError;

    return (
        <>
            <InputRow>
                <LabeledText label="Media Types" childClassName="flex flex-row space-x-4">
                    <CheckboxInput label="Video" checked={!!request.includeVideo} onChange={v => helpers.updateModel("includeVideo", !!v.currentTarget.checked)} />
                    <CheckboxInput label="Radar" checked={!!request.includeRadarData} onChange={v => helpers.updateModel("includeRadarData", !!v.currentTarget.checked)} />
                    <CheckboxInput disabled={!hasVideoRecompress(device.firmwareVersion)} label="Recompress Video" checked={recompressVideo} onChange={(v) => setRecompressVideo(v.target.checked)} />
                </LabeledText>
            </InputRow>
            {recompressVideo && (
                <>
                    <InputRow>
                        <NumericInput label="Bitrate (bits / s)" value={request.desiredBps} onChange={handleBpsOnChange} />
                    </InputRow>
                    <InputRow>
                        <NumericInput label="Framerate (frames / s)" value={request.desiredFps} onChange={handleFpsOnChange} />
                    </InputRow>
                    <InputRow>
                        <NumericInput label="Width" value={request.desiredWidth} onChange={handleWidthOnChange} />
                    </InputRow>
                    <InputRow>
                        <NumericInput label="Height" value={request.desiredHeight} onChange={handleHeightOnChange} />
                    </InputRow>
                </>
            )}
            <InputRow>
                <span className="text-sm text-muted-body-text">All times are in <b>{displayZone}</b> ({tzAbbrev}).</span>
            </InputRow>
            <p className="m-5"></p>
            <InputRow>
                <SplitDateTimeInput label={startLabel} value={startDisplay} onChange={handlePeriodStartOnChange} fieldName="periodStart" validationError={startValidationError} validateModel={helpers.validateModel} />
            </InputRow>
            <InputRow>
                <SplitDateTimeInput label={endLabel} value={endDisplay} onChange={handlePeriodEndOnChange} fieldName="periodEnd" validationError={endValidationError} validateModel={helpers.validateModel} />
            </InputRow>
            {estimatedSizeBytes !== undefined && !hasPeriodError && (
                <InputRow>
                    <LabeledText label="Estimated Size">
                        <b>{formatByteSize(estimatedSizeBytes)}</b>
                    </LabeledText>
                </InputRow>
            )}
        </>
    );
};
